import {
  AgentDataListRequestSource,
  AgentDataSourceDetailType,
  AgentDataSourceType,
  AgentDocumentLinkType,
  CheckAllowUploadParams,
  DeleteAgentDocumentLinkParams,
  GGDriveAccounts,
  GGDriveData,
  GoogleDriveOAuthData,
  SharePointData,
  TrainGGDriveParams,
  TrainSharePointParams,
  UpdateDataSourceType,
  UpdateTagsDataSourceType,
  CheckExistFileParams,
  CheckExistFileResponse,
} from './types';

import axiosInstance from 'services/common/instance';

export const getAgentDataSourceByCodeService = async (
  code: string,
  params: AgentDataListRequestSource
): Promise<APIPagination<AgentDataSourceType[]>> => {
  const res = await axiosInstance.get(`agents/${code}/data-sources`, {
    params,
  });
  return res.data;
};

export const getAgentDataSourceDetailByCodeService = async ({
  agentCode,
  code,
}: {
  agentCode: string;
  code: string;
}): Promise<APIResponse<AgentDataSourceDetailType>> => {
  const res = await axiosInstance.get(
    `agents/${agentCode}/data-sources/${code}`
  );
  return res.data;
};

export const getAgentDataSourceDocumentLinksService = async (
  {
    agentCode,
    code,
  }: {
    agentCode: string;
    code: string;
  },
  params: BaseFilterParams
): Promise<APIPagination<AgentDocumentLinkType[]>> => {
  const res = await axiosInstance.get(
    `agents/${agentCode}/data-sources/${code}/links`,
    { params }
  );
  return res.data;
};

export const deleteDataSourceByCodeService = async (
  agentCode: string,
  codes: string[]
): Promise<void> => {
  await axiosInstance.delete(`agents/${agentCode}/data-sources`, {
    data: { codes },
  });
};

export const deleteDocumentLinksByCodeService = async ({
  agentCode,
  code,
  documentLinkCodes,
}: DeleteAgentDocumentLinkParams): Promise<void> => {
  await axiosInstance.delete(
    `agents/${agentCode}/data-sources/${code}/document-links`,
    { data: { codes: documentLinkCodes } }
  );
};

export const resyncDataSourceByCodeService = async (
  agentCode: string,
  code: string
): Promise<void> => {
  await axiosInstance.post(`agents/${agentCode}/data-sources/re-sync/${code}`);
};

export const updateTagsDataSourceByCodeService = async (
  agentCode: string,
  params: UpdateTagsDataSourceType
): Promise<void> => {
  await axiosInstance.post(
    `agents/${agentCode}/data-sources/update-tags`,
    params
  );
};

export const getAgentDetailService = async (
  agentCode: string,
  dataSrcCode: string
): Promise<AgentDataSourceType> => {
  const res = await axiosInstance.get(
    `agents/${agentCode}/data-sources/${dataSrcCode}`
  );
  return res.data.data;
};

export const updateDataSourceService = async (
  agentCode: string,
  code: string,
  params: UpdateDataSourceType
): Promise<void> => {
  await axiosInstance.put(`agents/${agentCode}/data-sources/${code}`, params);
};

export const getSharePointFileService = async ({
  agentCode,
  siteId,
  driveId,
  itemId,
}: {
  agentCode: string;
  siteId?: string;
  driveId?: string;
  itemId?: string;
}): Promise<SharePointData[]> => {
  const response = await axiosInstance.get(
    `agent-sharepoints/${agentCode}`,
    {
      params: {
        siteId,
        driveId,
        itemId,
      },
    }
  );
  return response.data.data;
};

export const getSharePointShareService = async ({
  agentCode,
}: {
  agentCode: string;
}): Promise<SharePointData[]> => {
  const response = await axiosInstance.get(
    `agent-sharepoints/${agentCode}/shared-with-me`
  );
  return response.data.data;
};

export const trainSharePointService = async (
  params: TrainSharePointParams
) => {
  const { agentCode, ...body } = params;
  const response = await axiosInstance.post(`agent-sharepoints/${agentCode}/embedding`, body);
  return response.data.data;
};

export const checkAlowUploadService = async (
  params: CheckAllowUploadParams
) => {
  const { agentCode, ...body } = params;
  const response = await axiosInstance.post(`agents/${agentCode}/data-sources/files/allow-upload`, body);
  return response.data.data;
};

export const getDriveFileService = async ({
  agentCode,
  parentId,
}: {
  agentCode: string;
  parentId?: string;
}): Promise<GGDriveData[]> => {
  const response = await axiosInstance.get(
    `agent-google-drives/${agentCode}?parentId=${parentId}`
  );
  return response.data.data;
};

export const getDriveShareService = async ({
  agentCode,
}: {
  agentCode: string;
}): Promise<GGDriveData[]> => {
  const response = await axiosInstance.get(
    `agent-google-drives/${agentCode}/shared-with-me`
  );
  return response.data.data;
};

export const getDriveShareAccountService = async ({
  agentCode,
}: {
  agentCode: string;
}): Promise<GGDriveAccounts> => {
  const response = await axiosInstance.get(
    `agent-google-drives/${agentCode}/gg-accounts`
  );
  return response.data.data;
};

export const getSharePointAccountService = async ({
  agentCode,
}: {
  agentCode: string;
}): Promise<GGDriveAccounts> => {
  const response = await axiosInstance.get(
    `agent-sharepoints/${agentCode}/ms-accounts`
  );
  return response.data.data;
};

export const trainDriveService = async (
  params: TrainGGDriveParams
) => {
  const { agentCode, ...body } = params;
  const response = await axiosInstance.post(`agent-google-drives/${agentCode}/embedding`, body);
  return response.data.data;
};

export const getOAuthGoogleDriveService = async (): Promise<GoogleDriveOAuthData> => {
  const response = await axiosInstance.get('customers/configs/social?provider=google');
  return response.data.data;
};

export const refreshTokenGGDrive = async ({
  agentCode,
  code
}: {
  agentCode: string;
  code: string
}) => {
  const response = await axiosInstance.post(`agent-google-drives/${agentCode}/gg-refresh-token`, { code });
  return response.data.data;
};

export const disconnectGGDrive = async ({
  agentCode,
  code
}: {
  agentCode: string;
  code: string
}) => {
  const response = await axiosInstance.post(`agent-google-drives/${agentCode}/disconnect-account`, { code });
  return response.data.data;
};

export const removeAccountGGDrive = async ({
  agentCode,
  code
}: {
  agentCode: string;
  code: string
}) => {
  const response = await axiosInstance.post(`agent-google-drives/${agentCode}/remove-account`, { code });
  return response.data.data;
};

export const refreshTokenMS = async ({
  agentCode,
  code
}: {
  agentCode: string;
  code: string
}) => {
  const response = await axiosInstance.post(`agent-sharepoints/${agentCode}/ms-refresh-token`, { code });
  return response.data.data;
};

export const disconnectMS = async ({
  agentCode,
  code
}: {
  agentCode: string;
  code: string
}) => {
  const response = await axiosInstance.post(`agent-sharepoints/${agentCode}/disconnect-account`, { code });
  return response.data.data;
};

export const removeAccountMS = async ({
  agentCode,
  code
}: {
  agentCode: string;
  code: string
}) => {
  const response = await axiosInstance.post(`agent-sharepoints/${agentCode}/remove-account`, { code });
  return response.data.data;
};

export const viewDocument = async (
  agentCode: string,
  code: string
) => {
  const response = await axiosInstance.get(`agents/${agentCode}/data-sources/${code}/view-document`,);
  return response.data.data;
};

export const checkExistFileService = async (
  agentCode: string,
  params: CheckExistFileParams
): Promise<APIResponse<CheckExistFileResponse>> => {
  const res = await axiosInstance.post(
    `agents/${agentCode}/data-sources/files/check-exist`,
    params
  );
  return res.data;
};

export const reembedDataSourceService = async (
  agentCode: string,
  fileCodes: string[]
): Promise<void> => {
  await axiosInstance.post(`agents/${agentCode}/data-sources/embedding/reembed`, {
    fileCodes,
  });
};
